import React, { useState } from 'react'
import { File, FileText, Image, Download, Trash2, ExternalLink, AlertCircle } from 'lucide-react'
import './FileCard.css'

/**
 * FileCard component for displaying file metadata with actions
 * Supports preview, download, and delete operations
 */
function FileCard({ file, patientId, onDelete, onDownload }) {
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)

  // Determine file icon based on MIME type
  const getFileIcon = (mimeType) => {
    if (!mimeType) return <File size={24} />
    
    if (mimeType.startsWith('image/')) {
      return <Image size={24} />
    } else if (mimeType === 'application/pdf' || mimeType.includes('document')) {
      return <FileText size={24} />
    }
    return <File size={24} />
  }

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Format upload date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date'
    try {
      return new Date(timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Unknown date'
    }
  }

  // Handle file download
  const handleDownload = () => {
    if (onDownload) {
      onDownload(file)
    }
  }

  // Handle file deletion
  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${file.filename}"?`)) {
      return
    }

    try {
      setDeleting(true)
      setError(null)
      await onDelete(file.file_id)
    } catch (err) {
      console.error('Error deleting file:', err)
      setError(err.message || 'Failed to delete file')
      setDeleting(false)
    }
  }

  // Check if file is an image that can be previewed
  const isPreviewable = file.mime_type?.startsWith('image/')

  return (
    <div className="file-card">
      <div className="file-card-icon">
        {getFileIcon(file.mime_type)}
      </div>
      
      <div className="file-card-info">
        <div className="file-card-name" title={file.filename}>
          {file.filename}
        </div>
        <div className="file-card-meta">
          <span className="file-size">{formatFileSize(file.size)}</span>
          <span className="file-date">{formatDate(file.upload_timestamp)}</span>
        </div>
      </div>

      <div className="file-card-actions">
        {isPreviewable && (
          <button
            className="file-action-btn preview"
            onClick={() => window.open(file.preview_url, '_blank')}
            title="Preview in new tab"
            disabled={!file.preview_url}
          >
            <ExternalLink size={16} />
          </button>
        )}
        
        <button
          className="file-action-btn download"
          onClick={handleDownload}
          title="Download file"
        >
          <Download size={16} />
        </button>
        
        <button
          className="file-action-btn delete"
          onClick={handleDelete}
          disabled={deleting}
          title="Delete file"
        >
          {deleting ? (
            <span className="spinner-small" />
          ) : (
            <Trash2 size={16} />
          )}
        </button>
      </div>

      {error && (
        <div className="file-card-error">
          <AlertCircle size={14} />
          {error}
        </div>
      )}
    </div>
  )
}

export default FileCard